// ============================================================
// PanoramaDoNucleo.js — os quatro números do núcleo
//
// Início e Relatórios mostravam os mesmos quatro números, cada um com a
// sua cópia: títulos diferentes ("Panorama do núcleo" e "Visão geral"),
// ordem diferente e textos de apoio diferentes para o mesmo dado. Quem
// abria as duas telas via dois painéis e ficava sem saber se eram a mesma
// conta.
//
// Os números vêm de dadosDoNucleo, que já era compartilhado. O que faltava
// era a apresentação vir junto.
// ============================================================

import { useState } from "react";
import { useDadosDoNucleo } from "../dadosDoNucleo";
import { useNomeNucleo } from "../NucleosContexto";
import {
  MESES, AZUL, AZUL_ESCURO, VERDE_LOGO, CINZA_ESC, TEXTO, BRANCO,
  BORDA, RAIO, ESP, SOMBRA, TIPO, OK, ATENCAO, RUIM,
} from "../tema";
import Card from "./Card";
import { Estatistica, FileiraDeEstatisticas, Barra, Carregando } from "./Dados";
import useIsMobile from "../useIsMobile";
import SemNucleo from "./SemNucleo";

// A cor do percentual segue a leitura dos relatórios: abaixo de 75 é
// problema, de 75 a 85 é atenção. Número sem cor obriga a lembrar a régua.
export function corDoPct(pct) {
  if (pct === null || pct === undefined) return CINZA_ESC;
  if (pct >= 85) return OK;
  if (pct >= 75) return ATENCAO;
  return RUIM;
}

export default function PanoramaDoNucleo({ nucleo, onIr, mostrarQuemRegistrou = false }) {
  const nomeNucleo = useNomeNucleo();
  const isMobile = useIsMobile();
  const d = useDadosDoNucleo(nucleo);
  const [mostrarHoje, setMostrarHoje] = useState(false);

  const { atletasAtivos, registrosHoje, registrosMes, pctMes, presentesHoje, mesNum, ano } = d;
  const hojeBR = new Date().toLocaleDateString("pt-BR");
  const temPresentes = mostrarQuemRegistrou && (presentesHoje || []).length > 0;

  // Sem núcleo escolhido, a tela diz isso em vez de mostrar os números
  // de outra pessoa. Depois dos hooks, de propósito: sair antes deles
  // quebraria a ordem que o React exige.
  if (!nucleo) return <SemNucleo oQueFaria="ver o panorama deste núcleo" />;

  return (
    <Card
      titulo="Panorama do núcleo"
      sub={`${MESES[mesNum]}/${ano} · Núcleo ${nomeNucleo(nucleo) || nucleo || "—"} · ${hojeBR}`}
      style={{ marginBottom: ESP.xl }}
    >
      {d.carregando ? (
        <Carregando linhas={4} />
      ) : (
        <FileiraDeEstatisticas>
          <Estatistica
            valor={atletasAtivos === null ? "—" : atletasAtivos}
            rotulo="Atletas ativos" contexto="cadastrados no núcleo"
            cor={AZUL} acao={onIr ? () => onIr("atletas") : undefined}
          />
          <Estatistica
            valor={pctMes === null ? "—" : `${pctMes}%`}
            rotulo="Frequência do mês" contexto="média até hoje"
            cor={corDoPct(pctMes)} acao={onIr ? () => onIr("relatorio") : undefined}
          />
          {/* Quem registrou hoje sai ao passar o mouse, e não como cartão
              próprio: na tela de Relatórios não há espaço para uma lista, e
              o número sozinho não diz se falta alguém. No Início a lista
              tem cartão só dela, e por isso a dica não aparece lá. */}
          <div style={{ position: "relative" }}
            onClick={() => { if (temPresentes && isMobile) setMostrarHoje((v) => !v); }}
            onMouseEnter={() => { if (temPresentes && !isMobile) setMostrarHoje(true); }}
            onMouseLeave={() => { if (temPresentes && !isMobile) setMostrarHoje(false); }}
          >
            <Estatistica
              valor={registrosHoje === null ? "—" : registrosHoje}
              rotulo="Registros de hoje"
              contexto={temPresentes
                ? (isMobile ? "toque para ver quem" : "passe o mouse para ver quem")
                : "presenças até agora"}
              cor={VERDE_LOGO}
              acao={!temPresentes && onIr ? () => onIr("presenca_fisica") : undefined}
            />

            {temPresentes && mostrarHoje && (
              <div style={{
                position: isMobile ? "static" : "absolute",
                top: isMobile ? "auto" : "calc(100% + 6px)",
                right: isMobile ? "auto" : 0,
                marginTop: isMobile ? ESP.sm : 0,
                zIndex: 20,
                background: BRANCO,
                border: `1px solid ${BORDA}`,
                borderRadius: RAIO.md,
                boxShadow: SOMBRA.suspenso,
                padding: "12px 14px",
                width: isMobile ? "100%" : 270,
                textAlign: "left",
              }}>
                <div style={{ ...TIPO.rotulo, color: CINZA_ESC, marginBottom: ESP.sm }}>
                  Registraram presença hoje
                </div>
                {presentesHoje.map((g) => (
                  <div key={g.modalidade} style={{ marginBottom: ESP.sm }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: AZUL_ESCURO }}>
                      {g.modalidade} · {g.nomes.length}
                    </div>
                    <div style={{ fontSize: 12, color: TEXTO, lineHeight: 1.5 }}>
                      {g.nomes.join(", ")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Estatistica
            valor={registrosMes === null ? "—" : registrosMes}
            rotulo="Registros no mês" contexto={MESES[mesNum]}
            cor={AZUL} acao={onIr ? () => onIr("relatorio") : undefined}
          />
        </FileiraDeEstatisticas>
      )}

      {pctMes !== null && (
        <div style={{ marginTop: ESP.lg }}>
          <Barra pct={pctMes} cor={corDoPct(pctMes)} altura={8} />
        </div>
      )}
    </Card>
  );
}
